import { queryCommonFlags } from "@/api/constants";
import GenericAccordion from "@/modules/common/components/ui-kit/generic-accordion";
import { getNawyNowRecommendations } from "@/modules/lead-page/api/lead-page.v2.api";
import PropertyCard from "@/modules/lead-page/new/components/recommendations/property-card";
import PropertyCardSkeleton from "@/modules/lead-page/new/components/recommendations/property-card-skeleton";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useMemo } from "react";
import { Stack, Typography, useTheme } from "@mui/material";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";

export interface Property {
  id: number;
  path: string;
  propertyType: string;
  compoundName: string;
  compoundSlug: string;
  areaName: string;
  numberOfBathrooms: number;
  numberOfBedrooms: number;
  unitArea: number;
  developerLogoUrl: string;
  isLaunchSummary: number;
  coverImageUrl: string;
  deliveryIn: number | null;
  installmentYears: number;
  totalPrice: number;
  installmentType: string;
  currency: string;
  monthlyInstallment: number;
}

const Recommendations = () => {
  const theme = useTheme();
  const { lead } = useContext(LeadPageContext);

  const propertyId = useMemo(
    () => lead?.data?.propertyId ?? null,
    [lead]
  );

  const queryEnabled = !!propertyId;

  const {
    data: recommendations,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["nawy-now-recommendations", propertyId],
    queryFn: () => getNawyNowRecommendations(propertyId as number),
    enabled: queryEnabled,
    staleTime: Infinity,
    ...queryCommonFlags,
  });

  const renderRecommendations = useCallback(() => {
    if (!queryEnabled) {
      return (
        <Typography variant="body2" color="text.secondary">
          No recommendations available.
        </Typography>
      );
    }

    if (isLoading || isFetching) {
      return (
        <Stack gap={2}>
          {Array.from({ length: 3 }).map((_, idx) => (
            <PropertyCardSkeleton key={idx} />
          ))}
        </Stack>
      );
    }

    if (isError) {
      return (
        <Typography color="error" variant="body2">
          Failed to load recommendations.
        </Typography>
      );
    }

    if (!recommendations || recommendations?.data?.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No recommendations available.
        </Typography>
      );
    }

    return (
      <Stack gap={theme.nawy.spacing.md}>
        {recommendations.data.map((property: Property) => (
          <PropertyCard key={property.id} property={property} />
        ))}
      </Stack>
    );
  }, [queryEnabled, isLoading, isFetching, isError, recommendations, theme]);

  return (
    <GenericAccordion title="Recommendations" defaultExpanded rounded>
      {renderRecommendations()}
    </GenericAccordion>
  );
};

export default Recommendations;
